import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { IcsrmUniEnum } from '@adornis/bh-plugin-impl/stammdaten/icsrm-stammdaten-plugin.js';
import { Language } from '@adornis/translation-core/languages.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { maxLength, minLength } from '@adornis/validation/functions/length.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { isScoreRelevantChange } from '../shared/matching-score-invalidation.js';
import { EmploymentType, ExpectedAcademicTitle, PreferredStudyField, SoftSkills, SpeechLevel } from './job-position.js';
import { MatchingScoreCache } from './MatchingScoreCache.js';

@Entity()
export class OtherSpeechLevels extends AdornisEntity {
  static override _class = 'OtherSpeechLevels';

  constructor(doc: PartialEntityData<OtherSpeechLevels>) {
    super(doc);
  }

  @Field(() => String)
  _id!: string;

  @Field(() => String)
  language: Maybe<string>;

  @Field(() => SpeechLevel)
  level: Maybe<SpeechLevel>;
}

@Entity()
export class Motivation extends AdornisEntity {
  static override _class = 'Motivation';

  constructor(doc: PartialEntityData<Motivation>) {
    super(doc);
  }

  @Field(() => ID)
  _id!: string;

  @Field(() => String)
  jobPositionID: Maybe<string>;

  @validate(and(minLength(60, 'validation_motivation_too_short'), maxLength(2000, 'validation_motivation_too_long')))
  @Field(() => String)
  motivation: Maybe<string>;
}

export enum PracticalExperienceLevel {
  NONE = 'NONE',
  FIRST = 'FIRST',
  EXTENSIVE = 'EXTENSIVE',
  UNKNOWN = 'UNKNOWN',
}

registerEnum({ enum: PracticalExperienceLevel, name: 'PracticalExperienceLevel' });

@Entity()
export class PracticalExperience extends AdornisEntity {
  static override _class = 'PracticalExperience';

  constructor(doc: PartialEntityData<PracticalExperience>) {
    super(doc);
  }

  @Field(() => ID)
  _id!: string;

  @Field(() => String)
  description: Maybe<string>;

  @Field(() => PracticalExperienceLevel)
  level: Maybe<PracticalExperienceLevel>;
}

export const MAX_SOFT_SKILLS = 5;

// we need to store user cv data in a new collection because we do not want to mess with the buchungsheld user as we want to use the same users and DB for both Matching Week and Buchungsheld TuDa
@Entity()
export class MWUserData extends MongoEntity {
  static override _class = 'MWUserData';
  static override _collectionName = 'mw-user-data';

  constructor(doc: PartialEntityData<MWUserData>) {
    super(doc);
  }

  @Field(() => ID)
  userID!: string;

  @Field(() => ID)
  companyID: Maybe<string>;

  @Field(() => String)
  firstName: Maybe<string>;

  @Field(() => String)
  lastName: Maybe<string>;

  @validate(nonOptional())
  @Field(() => IcsrmUniEnum)
  university: Maybe<IcsrmUniEnum>;

  @validate(nonOptional())
  @Field(() => PreferredStudyField)
  department: Maybe<PreferredStudyField>;

  @validate(nonOptional())
  @Field(() => String)
  studyField: Maybe<string>;

  @validate(nonOptional())
  @Field(() => ExpectedAcademicTitle)
  academicTitle: Maybe<ExpectedAcademicTitle>;

  @validate(nonOptional())
  @Field(() => Date)
  expectedCompletionDate: Maybe<Date>;

  // ISO 3166-1 alpha-2 country code as emitted by <x-country-picker>.
  @validate(nonOptional())
  @Field(() => String)
  nationality: Maybe<string>;

  // ISO 3166-1 alpha-2 country code as emitted by <x-country-picker>.
  @validate(nonOptional())
  @Field(() => String)
  countryOfBirth: Maybe<string>;

  // "Zuwanderungsgeschichte" — true if the user (or their family) immigrated.
  // `allowFalsy: true` so `false` (the "Nein" answer) isn't treated as missing
  // — by default nonOptional() rejects any falsy value including booleans.
  @validate(nonOptional({ allowFalsy: true }))
  @Field(() => Boolean)
  hasMigrationBackground: Maybe<boolean>;

  @validate(nonOptional())
  @Field(() => Date)
  dateOfBirth: Maybe<Date>;

  @validate(nonOptional())
  @Field(() => SpeechLevel)
  languageSkillsDE: Maybe<SpeechLevel>;

  @validate(nonOptional())
  @Field(() => SpeechLevel)
  languageSkillsEN: Maybe<SpeechLevel>;

  @Field(() => [OtherSpeechLevels])
  otherLanguages: Maybe<OtherSpeechLevels[]>;

  @validate(nonOptional())
  @Field(() => String)
  techSkills: Maybe<string>;

  @validate(
    and(minLength(1, 'validation_soft_skills_too_few'), maxLength(MAX_SOFT_SKILLS, 'validation_soft_skills_too_many')),
  )
  @Field(() => [SoftSkills])
  softSkills: Maybe<SoftSkills[]>;

  @Field(() => [PracticalExperience])
  practicalExperiences: Maybe<PracticalExperience[]>;

  @Field(() => ID)
  resumeFileId: Maybe<string>;

  @Field(() => [Motivation])
  motivations: Maybe<Motivation[]>;

  @Field(() => [EmploymentType])
  desiredEmploymentTypes: Maybe<EmploymentType[]>;

  @Field(() => Boolean)
  privacyConsent: Maybe<boolean>;

  @Field(() => Boolean)
  accuracyConfirm: Maybe<boolean>;

  @Field(() => String, { default: v => v ?? Language.GERMAN })
  chosenLanguage: Maybe<string>;

  static override async transformBeforeUpdate(
    oldEntity: PartialEntityData<MWUserData>,
    update: MWUserData,
  ): Promise<void> {
    if (isScoreRelevantChange(oldEntity as Record<string, unknown>, update.toObject() as Record<string, unknown>)) {
      // Invalidate cached matching scores on score-relevant updates -> They will be recalculated on next load
      const matchingScoreCacheCol = mongo().collection<MatchingScoreCache>(MatchingScoreCache._collectionName);
      await matchingScoreCacheCol.deleteMany({ userID: oldEntity.userID });
    }

    return super.transformBeforeUpdate(oldEntity, update);
  }
}
